import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Close, EventRepeat, Edit } from '@mui/icons-material';
import {
    fetchWithdrawSetting,
    fetchWithdrawPolicies,
    fetchWithdrawalProviders,
    saveWithdrawSetting,
} from '../../services/withdrawalService';

const PERIODICITY_LABELS = {
    free: 'Libre (à la demande)',
    daily: 'Chaque jour',
    weekly: 'Chaque semaine',
    monthly: 'Chaque mois',
};

const WEEK_DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

const pad = (n) => String(n ?? 0).padStart(2, '0');

/** Canal de traitement (ProcessDelay) de la politique correspondant au type d'opérateur choisi. */
const resolveProcessDelayId = (policy, provider) => {
    const delays = policy?.processDelays || [];
    if (!delays.length) return null;
    const type = String(provider?.provider_type || '').toLowerCase();
    const category = type.includes('bank') || type.includes('banque') ? 'bank' : 'mobile';
    const match = delays.find((d) => String(d.providerType || '').toLowerCase().includes(category));
    return (match || delays[0]).id;
};

const describeSchedule = (setting) => {
    if (!setting) return '—';
    const periodicity = setting.periodicity;
    if (!setting.withdrawPolicy?.auto_request || periodicity === 'free') return PERIODICITY_LABELS[periodicity] || periodicity;
    const time = `${pad(setting.hour)}:${pad(setting.minute)}`;
    if (periodicity === 'weekly') return `Chaque ${(WEEK_DAYS[(setting.day || 1) - 1] || '').toLowerCase()} à ${time}`;
    if (periodicity === 'monthly') return `Le ${setting.day} de chaque mois à ${time}`;
    return `Chaque jour à ${time}`;
};

const inputClass = 'w-full px-3 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 bg-white';

const WithdrawPolicyModal = ({ show, onClose, currentSetting, ownerId, onSaved }) => {
    const [policies, setPolicies] = useState([]);
    const [providers, setProviders] = useState([]);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!show) return;
        setError(null);
        Promise.all([fetchWithdrawPolicies(), fetchWithdrawalProviders()])
            .then(([policyList, providerList]) => {
                setPolicies(policyList);
                setProviders(providerList);
                const current = currentSetting;
                const policy = policyList.find((p) => p.id === (current?.withdraw_policy_id || current?.withdrawPolicy?.id))
                    || policyList.find((p) => p.useByDefault) || policyList[0];
                const method = current?.withdrawMethod || {};
                setForm({
                    policy_id: policy?.id || '',
                    periodicity: current?.periodicity || policy?.periodicities?.[0] || 'free',
                    day: current?.day ?? 1,
                    hour: current?.hour ?? 8,
                    minute: current?.minute ?? 0,
                    method: {
                        id: method.id || null,
                        provider_id: method.provider_id || '',
                        account: method.account || '',
                        full_name: method.full_name || '',
                        prefix: method.prefix || '',
                        suffix: method.suffix || '',
                        email_address: method.email_address || '',
                    },
                });
            })
            .catch((e) => setError(e?.response?.data?.message || e.message || 'Impossible de charger les politiques de retrait'));
    }, [show, currentSetting]);

    const policy = useMemo(() => policies.find((p) => p.id === form?.policy_id), [policies, form?.policy_id]);
    const provider = useMemo(() => providers.find((p) => p.id === form?.method?.provider_id), [providers, form?.method?.provider_id]);

    if (!show) return null;

    const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
    const setMethod = (field, value) => setForm((prev) => ({ ...prev, method: { ...prev.method, [field]: value } }));
    const selectPolicy = (p) => setForm((prev) => ({
        ...prev,
        policy_id: p.id,
        periodicity: p.periodicities?.includes(prev.periodicity) ? prev.periodicity : (p.periodicities?.[0] || 'free'),
    }));

    const scheduled = policy?.auto_request && form?.periodicity !== 'free';

    const handleSubmit = async (e) => {
        e.preventDefault();
        const processDelayId = resolveProcessDelayId(policy, provider);
        const checks = [
            [!policy, 'Choisissez une politique de retrait.'],
            [!form.method.provider_id, 'Choisissez un opérateur de retrait.'],
            [provider?.with_account && !form.method.account.trim(), `Renseignez ${provider?.account_label || 'le numéro de compte'}.`],
            [provider?.with_account && provider?.account_format && !new RegExp(provider.account_format).test(form.method.account.trim()),
                `Le format de ${provider?.account_label || 'compte'} est invalide.`],
            [provider?.with_full_name && !form.method.full_name.trim(), 'Renseignez le nom complet du titulaire.'],
            [provider?.with_email_address && !form.method.email_address.trim(), "Renseignez l'adresse email."],
            [!processDelayId, "Cette politique n'a pas de canal de traitement configuré. Contactez le support."],
        ];
        const failed = checks.find(([bad]) => bad);
        if (failed) return setError(failed[1]);

        setSaving(true);
        setError(null);
        try {
            await saveWithdrawSetting({
                owner: ownerId,
                policy_id: policy.id,
                periodicity: form.periodicity,
                process_delay_id: processDelayId,
                day: scheduled ? Number(form.periodicity === 'daily' ? 1 : form.day) : null,
                hour: scheduled ? Number(form.hour) : null,
                minute: scheduled ? Number(form.minute) : null,
                withdrawMethod: {
                    id: form.method.id,
                    provider_id: form.method.provider_id,
                    account: form.method.account.trim() || null,
                    full_name: form.method.full_name.trim() || null,
                    prefix: form.method.prefix.trim() || null,
                    suffix: form.method.suffix.trim() || null,
                    email_address: form.method.email_address.trim() || null,
                    expiration_date: null,
                    control: null,
                    owner: ownerId,
                },
            });
            onSaved?.();
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <form onSubmit={handleSubmit} className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl">
                <div className="flex items-center justify-between p-6 border-b border-slate-200">
                    <h3 className="text-xl font-bold text-slate-900">Politique de retrait</h3>
                    <button type="button" onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 text-slate-500" aria-label="Fermer">
                        <Close />
                    </button>
                </div>

                {!form ? (
                    <div className="p-10 text-center text-slate-500 text-sm">{error || 'Chargement…'}</div>
                ) : (
                    <div className="p-6 space-y-6">
                        <section className="space-y-2">
                            <p className="text-sm font-semibold text-slate-800">Formule</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {policies.map((p) => {
                                    const selected = p.id === form.policy_id;
                                    return (
                                        <button type="button" key={p.id} onClick={() => selectPolicy(p)}
                                            className={`text-left rounded-xl border p-4 transition-colors ${selected ? 'border-amber-400 bg-amber-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="font-semibold text-slate-900">{p.title}</span>
                                                {p.badge && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-md bg-amber-500 text-white">{p.badge}</span>}
                                            </div>
                                            {p.description && <p className="text-xs text-slate-600 mt-1">{p.description}</p>}
                                            {(p.withdrawPolicyLines || []).slice(0, 2).map((l) => (
                                                <p key={l.id} className="text-xs text-slate-500 mt-1">
                                                    Frais {l.fees} % · {l.min_amount}–{l.max_amount} {l.currency}
                                                </p>
                                            ))}
                                            <p className="text-xs text-slate-500 mt-1">{p.auto_request ? 'Retrait automatique programmé' : 'Retrait à la demande'}</p>
                                        </button>
                                    );
                                })}
                            </div>
                        </section>

                        <section className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className={scheduled ? '' : 'sm:col-span-2'}>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Périodicité</label>
                                <select value={form.periodicity} onChange={(e) => setField('periodicity', e.target.value)} className={inputClass}>
                                    {(policy?.periodicities?.length ? policy.periodicities : ['free']).map((p) => (
                                        <option key={p} value={p}>{PERIODICITY_LABELS[p] || p}</option>
                                    ))}
                                </select>
                            </div>
                            {scheduled && (
                                <div className="grid grid-cols-3 gap-2">
                                    {form.periodicity === 'weekly' && (
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1">Jour</label>
                                            <select value={form.day} onChange={(e) => setField('day', e.target.value)} className={inputClass}>
                                                {WEEK_DAYS.map((d, i) => <option key={d} value={i + 1}>{d}</option>)}
                                            </select>
                                        </div>
                                    )}
                                    {form.periodicity === 'monthly' && (
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1">Jour</label>
                                            <select value={form.day} onChange={(e) => setField('day', e.target.value)} className={inputClass}>
                                                {Array.from({ length: 31 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
                                            </select>
                                        </div>
                                    )}
                                    <div className={form.periodicity === 'daily' ? 'col-span-2' : ''}>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Heure</label>
                                        <select value={form.hour} onChange={(e) => setField('hour', e.target.value)} className={inputClass}>
                                            {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{pad(i)} h</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Minute</label>
                                        <select value={form.minute} onChange={(e) => setField('minute', e.target.value)} className={inputClass}>
                                            {[0, 15, 30, 45].map((m) => <option key={m} value={m}>{pad(m)}</option>)}
                                        </select>
                                    </div>
                                </div>
                            )}
                        </section>

                        <section className="space-y-3">
                            <p className="text-sm font-semibold text-slate-800">Compte de destination</p>
                            <select value={form.method.provider_id} onChange={(e) => setMethod('provider_id', e.target.value)} className={inputClass}>
                                <option value="">— Choisir un opérateur —</option>
                                {providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                            {provider?.with_account && (
                                <input type="text" value={form.method.account} onChange={(e) => setMethod('account', e.target.value)}
                                    className={inputClass} placeholder={provider.account_placeholder || provider.account_label || 'Numéro de compte'} />
                            )}
                            {provider?.with_full_name && (
                                <input type="text" value={form.method.full_name} onChange={(e) => setMethod('full_name', e.target.value)}
                                    className={inputClass} placeholder="Nom complet du titulaire" />
                            )}
                            {(provider?.with_prefix || provider?.with_suffix) && (
                                <div className="grid grid-cols-2 gap-3">
                                    {provider.with_prefix && (
                                        <input type="text" value={form.method.prefix} onChange={(e) => setMethod('prefix', e.target.value)}
                                            className={inputClass} placeholder="Préfixe" />
                                    )}
                                    {provider.with_suffix && (
                                        <input type="text" value={form.method.suffix} onChange={(e) => setMethod('suffix', e.target.value)}
                                            className={inputClass} placeholder="Suffixe" />
                                    )}
                                </div>
                            )}
                            {provider?.with_email_address && (
                                <input type="email" value={form.method.email_address} onChange={(e) => setMethod('email_address', e.target.value)}
                                    className={inputClass} placeholder="Adresse email" />
                            )}
                        </section>

                        {error && <p className="text-sm text-red-600">{error}</p>}

                        <div className="flex justify-end gap-3 pt-2 border-t border-slate-200">
                            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-xl border border-slate-300 text-slate-700 hover:bg-slate-50">
                                Annuler
                            </button>
                            <button type="submit" disabled={saving} className="px-6 py-2.5 rounded-xl bg-amber-500 text-white font-semibold hover:bg-amber-600 disabled:opacity-50">
                                {saving ? 'Enregistrement…' : 'Enregistrer'}
                            </button>
                        </div>
                    </div>
                )}
            </form>
        </div>
    );
};

/** Carte « Politique de retrait » de l'organisation, avec modification. */
const WithdrawPolicySection = ({ organizationId, solutionId, currency = 'CDF' }) => {
    const [setting, setSetting] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [open, setOpen] = useState(false);

    const load = useCallback(async () => {
        if (!organizationId || !solutionId) return;
        setLoading(true);
        setError(null);
        try {
            setSetting(await fetchWithdrawSetting(solutionId, currency, organizationId));
        } catch (e) {
            setError(e?.response?.data?.message || e.message || 'Impossible de charger la politique de retrait');
        } finally {
            setLoading(false);
        }
    }, [organizationId, solutionId, currency]);

    useEffect(() => { load(); }, [load]);

    const method = setting?.withdrawMethod;

    return (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
                        <EventRepeat className="text-amber-600" />
                    </div>
                    <div className="min-w-0">
                        <h2 className="text-lg font-semibold text-slate-800">Politique de retrait</h2>
                        {loading ? (
                            <p className="text-sm text-slate-500">Chargement…</p>
                        ) : error ? (
                            <p className="text-sm text-red-600">{error}</p>
                        ) : setting ? (
                            <div className="text-sm text-slate-600 space-y-0.5">
                                <p><span className="font-medium text-slate-800">{setting.withdrawPolicy?.title || 'Formule'}</span> · {describeSchedule(setting)}</p>
                                {method && <p>Vers {method.provider_name || 'compte'}{method.account ? ` · ${method.account}` : ''}</p>}
                                {setting.nextWithdraw && <p>Prochain retrait : {new Date(setting.nextWithdraw).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })}</p>}
                            </div>
                        ) : (
                            <p className="text-sm text-slate-500">Aucune politique configurée : les retraits se font à la demande.</p>
                        )}
                    </div>
                </div>
                <button type="button" onClick={() => setOpen(true)} disabled={!organizationId}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 text-sm font-medium hover:bg-amber-100 disabled:opacity-50 shrink-0">
                    <Edit fontSize="small" /> {setting ? 'Modifier' : 'Configurer'}
                </button>
            </div>
            <WithdrawPolicyModal show={open} onClose={() => setOpen(false)} currentSetting={setting} ownerId={organizationId} onSaved={load} />
        </div>
    );
};

export default WithdrawPolicySection;
